import { useEffect, useState } from 'react';
import { detectInstallTarget, isStandalone, type InstallTarget } from '../platform';
import { canPromptInstall, onInstallAvailabilityChange, promptInstall } from '../platform/install';

/**
 * Cihaza göre kurulum yolu. Tek uygulama: aksanenerji (PWA). Android, iPhone/iPad, Windows, macOS
 * ve Linux'ta ana ekrana / masaüstüne eklenir; ayrı mağaza, APK veya .exe yoktur.
 */
export function InstallGuide({ showAll = false }: { showAll?: boolean }) {
  const detected = detectInstallTarget();
  const [canPrompt, setCanPrompt] = useState(canPromptInstall());
  const [installed, setInstalled] = useState(isStandalone());
  const [tab, setTab] = useState<InstallTarget>(detected === 'unsupported' ? 'android' : detected);

  useEffect(() => onInstallAvailabilityChange(() => { setCanPrompt(canPromptInstall()); setInstalled(isStandalone()); }), []);

  if (installed) {
    return <div className="alert ok">aksanenerji bu cihazda uygulama olarak açık. Ana ekrandaki / masaüstündeki simgeden başlatabilirsiniz.</div>;
  }

  const tabs: [InstallTarget, string][] = [
    ['android', 'Android'], ['ios-safari', 'iPhone / iPad'], ['desktop', 'Windows / Linux / Mac (Chrome, Edge)'], ['mac-safari', 'Mac (Safari)'],
  ];
  const current = tab === 'ios-other' ? 'ios-safari' : tab;

  return (
    <div className="stack">
      {canPrompt && (
        <button className="btn primary block" onClick={() => void promptInstall().then(() => setCanPrompt(false))}>
          <img src="/icons/icon-192.png" alt="" width={24} height={24} style={{ borderRadius: 6 }} /> aksanenerji uygulamasını yükle
        </button>
      )}
      {(showAll || !canPrompt) && (
        <>
          {showAll && (
            <div className="tabs" role="tablist">
              {tabs.map(([k, l]) => (
                <button key={k} role="tab" aria-selected={current === k} className={current === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>
              ))}
            </div>
          )}
          <Steps target={current} detected={detected} />
        </>
      )}
    </div>
  );
}

function Steps({ target, detected }: { target: InstallTarget; detected: InstallTarget }) {
  switch (target) {
    case 'android':
      return (
        <ol className="small install-steps">
          <li>Bu sayfayı <strong>Chrome</strong> (veya Samsung İnternet / Edge) ile açın.</li>
          <li>Ekranda <strong>"Uygulamayı yükle"</strong> düğmesi çıkarsa ona dokunun.</li>
          <li>Çıkmazsa sağ üstteki <strong>⋮ menü → "Uygulamayı yükle"</strong> veya <strong>"Ana ekrana ekle"</strong>'yi seçin.</li>
          <li>Ana ekranda <strong>aksanenerji</strong> simgesi oluşur; uygulamayı oradan açın.</li>
        </ol>
      );
    case 'ios-safari':
    case 'ios-other':
      return (
        <>
          {detected === 'ios-other' && <div className="alert warn small">iPhone/iPad'de kurulum için bu sayfayı <strong>Safari</strong> ile açın.</div>}
          <ol className="small install-steps">
            <li>Bu sayfayı <strong>Safari</strong> ile açın.</li>
            <li>Alttaki (iPad'de üstteki) <strong>Paylaş</strong> düğmesine dokunun <span aria-hidden>(⬆︎ kutu simgesi)</span>.</li>
            <li>Listeyi aşağı kaydırıp <strong>"Ana Ekrana Ekle"</strong>'yi seçin.</li>
            <li>Ad olarak <strong>aksanenerji</strong> görünür; sağ üstte <strong>Ekle</strong>'ye dokunun.</li>
            <li>Uygulamayı ana ekrandaki <strong>aksanenerji</strong> simgesinden açın; kamera izni sorulursa <strong>İzin Ver</strong> deyin.</li>
          </ol>
        </>
      );
    case 'mac-safari':
      return (
        <ol className="small install-steps">
          <li>Safari menüsünden <strong>Dosya → Dock'a Ekle</strong>'yi seçin.</li>
          <li>Ad olarak <strong>aksanenerji</strong> görünür; <strong>Ekle</strong>'ye tıklayın.</li>
          <li>Uygulamayı Dock'taki veya Launchpad'deki simgeden açın.</li>
        </ol>
      );
    default:
      return (
        <ol className="small install-steps">
          <li>Bu sayfayı <strong>Google Chrome</strong> veya <strong>Microsoft Edge</strong> ile açın.</li>
          <li>Adres çubuğunun sağındaki <strong>Yükle</strong> simgesine <span aria-hidden>(⊕ / ekran ve ok)</span> tıklayın.</li>
          <li>Simge yoksa menüden: Chrome <strong>⋮ → Kaydet ve paylaş → "aksanenerji'yi yükle"</strong>; Edge <strong>… → Uygulamalar → "Bu siteyi uygulama olarak yükle"</strong>.</li>
          <li>aksanenerji kendi penceresinde açılır; Başlat menüsünde / Uygulamalar klasöründe ve masaüstünde simgesi oluşur.</li>
        </ol>
      );
  }
}
